import { FC, useState, useEffect, useMemo, useRef, useContext } from 'react';
import { useMethods } from '@lhb/hook';
import { useUnactivate } from 'react-activation';
import { getPipelineStatus, spotEvaluationDetail, spotEvaluationOverview } from '@/common/api/spotevaluation';
import { convertRadiusToDocValue, DrawerDataProps, SpotTypeEnum } from '../../ts-config';
import { getSelectionMapButton } from '@/common/api/siteselectionmap';
import { SemirModelCode } from '@/views/youth/pages/map/ts-config';
import { getPDFExportStatus } from '@/common/api/networkplan';
import { RangeSelectEnum } from '@/common/components/business/YouthMap/AreaDetailDrawer/components/FirstScreenRight/ts-config';
import styles from './index.module.less';
import V2Container from '@/common/components/Data/V2Container';
import V2Drawer from '@/common/components/Feedback/V2Drawer';
import Top from './components/Top';
import Main from './components/Main';
import SkeletonMapDetail from '@/common/components/Skeleton/SkeletonMapDetail';
import { typeEnum } from '@/views/surround/pages/index/ts-config';
import { useCurrentUserInfo } from '@/common/hook/useCurrentUserInfo';
import UserInfoContext from '@/layout/context';
// import { useSelector } from 'react-redux';

/**
 * 选址地图详情抽屉（实际上是点位测评详情抽屉）
 */
const AreaDetailDrawer: FC<{
  drawerData: DrawerDataProps;
  setDrawerData: (value: any) => void;
  onRefresh?: () => void;
}> = ({
  drawerData,
  setDrawerData,
  onRefresh,
}) => {
  const { tenantInfo } = useContext(UserInfoContext); // 租户信息
  const { agentTenant } = tenantInfo || {};

  const {
    /* 当前用户信息 */
    currentUserInfo,
  } = useCurrentUserInfo();
  // const tenantCheckData = useSelector((state: any) => state.common.tenantCheck);
  const [isSemirModel, setIsSemirModel] = useState<boolean>(false); // 是否是森马模型
  const intervalForPdfRef: any = useRef();
  const openTimeRef: any = useRef<any>(0);
  const [detail, setDetail] = useState<any>({}); // 详情信息
  const [overViewData, setOverViewData] = useState<any>({}); // 概览卡片
  const [loading, setLoading] = useState<boolean>(false); // 加载中
  const [configSwitch, setConfigSwitch] = useState<any>({});
  const [pdfDataStatus, setPdfDataStatus] = useState<any>(); // 商圈报告
  const [uploadPipelineStatus, setUploadPipelineStatus] = useState<any>(); // 上传流水线状态
  const intervalForUploadPipelineRef: any = useRef();
  const [isStartAnalyseOfAI, setIsStartAnalyseOfAI] = useState<any>(false); // 是否开始了AI分析
  // 是否是森马租户
  // const isSemir = useMemo(() => {
  //   const { isSenma } = tenantCheckData || {};
  //   return !!isSenma;
  // }, [tenantCheckData]);

  useEffect(() => {
    const { open, lng, lat, id } = drawerData;
    if (open && lng && lat) {
      // methods.eventTracking();
      methods.getDetail();
      if (id) {
        methods.getPDFStatus();
        methods.getUploadPipelineStatus();
      }
    }
    // 关闭弹窗时，将详情数据重置
    if (!open) {
      setDetail({});
      intervalForPdfRef.current && clearInterval(intervalForPdfRef.current);
      intervalForPdfRef.current = null;
      setIsStartAnalyseOfAI(false);
      // 在useEffect清理函数中添加
      intervalForUploadPipelineRef.current && clearInterval(intervalForUploadPipelineRef.current);
      intervalForUploadPipelineRef.current = null;
    }
  }, [drawerData]);

  // useEffect(() => {
  //   methods.getUploadPipelineStatus();
  // }, [uploadPipelineStatus]);

  const waterText = useMemo(() => {
    if (currentUserInfo?.name) {
      return currentUserInfo.mobile ? `${currentUserInfo.name} ${currentUserInfo.mobile.slice(-4)}` : currentUserInfo.name;
    }
    return '';
  }, [currentUserInfo]);

  // 是否是商场类型
  const isMallType = useMemo(() => {
    const { resourceMallFlag } = detail || {};
    return !!resourceMallFlag;
  }, [detail]);
  // 选址地图页被缓存,离开当前页面时，关闭抽屉
  useUnactivate(() => {
    methods.onCloseDrawer();
  });

  // methods
  const methods = useMethods({
    getDetail: async () => { // 获取商圈详情
      setLoading(true);
      const { lng, lat, poiName, type, modelClusterId, modelConfigId, poiSearchType, poiSearchMinute, poiSearchDistance, rangeMode, points } = drawerData as any;
      const radius = convertRadiusToDocValue({ poiSearchType, radius: drawerData.radius });
      const params: any = {
        type,
        modelConfigId,
      };
      if (type === SpotTypeEnum.Mall) {
        params.modelClusterId = modelClusterId;
      }
      if (type === SpotTypeEnum.Street) {
        params.lng = lng;
        params.lat = lat;
        params.poiSearchType = poiSearchType;
        params.radius = radius;
        // 时间/距离 tab 二选一透传
        params.poiSearchMinute = poiSearchMinute;
        params.poiSearchDistance = poiSearchDistance;
        params.points = points;
      }
      if (poiName) {
        params.poiName = poiName;
      }
      if (drawerData.id) {
        params.recordId = drawerData.id;
      }
      // const detailParams: any = {
      //   lng,
      //   lat,
      //   cityName,
      //   type,
      //   modelClusterId,
      //   modelConfigId
      // };
      // if (drawerData?.poiName) { // poi点位名称，兼容搜索地址时，高德返回的形成和服务端接口查询poi的名字不同时的字段
      //   detailParams.poiName = drawerData.poiName;
      // }
      Promise.all([
        spotEvaluationDetail(params),
        // spotEvaluationOverview(params),
        getSelectionMapButton({ modelConfigId }),
      ])
        .then(([detailRes, configRes]) => {

          /**
           * 以下逻辑同 src/views/spotevaluation/pages/detail/entry.tsx
           * 如果有修改，记得同步
           */

          /**
           * 大部分详情接口需要传入以下参数，放这里方便复制
            params.poiSearchType = detail?.poiSearchType;
            params.radius = detail?.radius;
            params.poiSearchMinute = detail?.poiSearchMinute;
            params.poiSearchDistance = detail?.poiSearchDistance;
            params.points = detail?.points;
           */

          // 接口说不会返回以下参数，所以这里手动赋值
          Object.assign(detailRes, {
            poiSearchType,
            rangeMode,
            radius,
            poiSearchMinute,
            poiSearchDistance,
          });

          if (poiSearchType !== typeEnum.radius) { // 详情接口没有围栏数据；分享详情接口有围栏数据
            // 优先使用接口的数据
            if (Array.isArray(detailRes.polygon) && detailRes.polygon.length) {
              // 地图绘制的围栏用这个参数，主要是不想再改组件用到的地方
              // detailRes.polygon = detailRes.polygon;
              // 其他接口的围栏入参用这个参数，主要是不想在用到的十几个接口重新转一遍
              detailRes.points = detailRes.polygon.map(item => ({ lng: item[0], lat: item[1] }));
            } else if (Array.isArray(points) && points.length) {
              // 地图绘制的围栏用这个参数，主要是不想再改组件用到的地方
              detailRes.polygon = points.map(item => [item.lng, item.lat]);
              // 其他接口的围栏入参用这个参数，主要是不想在用到的十几个接口重新转一遍
              detailRes.points = points;
            }
          }

          setDetail(detailRes);
          // setOverViewData(overviewRes);
          setConfigSwitch({
            ...configRes
          });
        })
        .catch((error) => {
          console.error('Error fetching details:', error);
        })
        .finally(() => {
          setLoading(false);
        });

      spotEvaluationOverview(params).then((res: any) => {
        const { modelConfigCode } = res || {};
        setIsSemirModel(modelConfigCode === SemirModelCode);
        setOverViewData(res);
      });
    },
    // 当前为选址地图埋点，需替换
    eventTracking: (isOpen = true) => { // 埋点
      if (isOpen) {
        openTimeRef.current = new Date().getTime();
        return;
      }
      // 关闭的时候上报总时长
      const curTimestamp = new Date().getTime();
      const timeLag = curTimestamp - openTimeRef.current; // 停留时长
      if (openTimeRef.current && curTimestamp && timeLag > 0) {
        window.LHBbigdata.send({
          event_id: 'dcf71805-ed9e-a3e9-1c09-85daf0d49e02', // 事件id
          msg: {
            viewTimestamp: timeLag
          }
        });
        openTimeRef.current = 0;
      }
    },
    onCloseDrawer() {
      setDrawerData({
        open: false,
        cityId: '',
        cityName: '',
        lng: '',
        lat: '',
        type: '',
        modelClusterId: '',
        modelConfigId: ''
      });
    },
    getPDFStatus: async () => { // 获取PDF导出状态
      const data = await getPDFExportStatus({
        id: drawerData.id,
        type: 3, // 1:商圈 2:机会点 3:点位测评
      });
      // exportStatus: 1:未导出 2: 导出完成 3: 导出中
      const { exportStatus } = data;
      setPdfDataStatus(data);
      // 当前是导出中而且还未创建定时器轮训
      if (exportStatus === 3 && !intervalForPdfRef.current) {
        intervalForPdfRef.current = setInterval(() => {
          methods.getPDFStatus();
        }, 5000);
      } else if (exportStatus === 2) { // 导出完成后，清空定时器
        intervalForPdfRef.current && clearInterval(intervalForPdfRef.current);
        intervalForPdfRef.current = null;
      }
    },
    getUploadPipelineStatus: async () => {
      // 获取上传流水线状态
      if (!drawerData.id) {
        return;
      }
      const data = await getPipelineStatus({
        id: drawerData.id,
      });
      const { uploadStatus } = data;
      setUploadPipelineStatus(uploadStatus);
      // 当前是上传中而且还未创建定时器轮训
      if (uploadStatus === 1 && !intervalForUploadPipelineRef.current) {
        intervalForUploadPipelineRef.current = setInterval(() => {
          methods.getUploadPipelineStatus();
        }, 5000);
      } else if (uploadStatus !== 1) { // 上传完成后，清空定时器
        intervalForUploadPipelineRef.current && clearInterval(intervalForUploadPipelineRef.current);
        intervalForUploadPipelineRef.current = null;
      }
    },
  });

  return (
    <V2Drawer
      bodyStyle={{
        padding: 0,
        paddingBottom: 16
      }}
      open={drawerData.open}
      destroyOnClose
      onClose={methods.onCloseDrawer}
      contentWrapperStyle={{
        width: '90%',
        minWidth: '921px', // 1024*0.9
        maxWidth: 'auto',
      }}
      className={styles.areaDetailDrawer}
    >
      {loading
        ? <SkeletonMapDetail/>
        : <V2Container
          // 容器上下padding 0， 所以减去就是0
          style={{ height: '100vh' }}
          extraContent={{
            top: <Top
              detail={detail}
              drawerData={drawerData}
              tenantId={drawerData.tenantId}
              configSwitch={configSwitch}
              // isSemirModel={isSemirModel}
              pdfDataStatus={pdfDataStatus}
              getPDFStatus={methods.getPDFStatus}
              uploadPipelineStatus={uploadPipelineStatus}
              getUploadPipelineStatus={methods.getUploadPipelineStatus}
              // 如果有半径就走半径，否则就是围栏
              rangeSelectValue={detail.radius || RangeSelectEnum.inPolygon}
              waterText={waterText}
              isStartAnalyseOfAI={isStartAnalyseOfAI}
              setIsStartAnalyseOfAI={setIsStartAnalyseOfAI}
              agentTenantData={agentTenant}
              setDetail={setDetail}
              onRefresh={onRefresh}
            />,
          }}
        >
          <Main
            drawerData={drawerData}
            detail={detail}
            overViewData={overViewData}
            isMallType={isMallType}
            configSwitch={configSwitch}
            tenantId={drawerData.tenantId}
            isSemirModel={isSemirModel}
            isStartAnalyseOfAI={isStartAnalyseOfAI}
            setIsStartAnalyseOfAI={setIsStartAnalyseOfAI}
            agentTenant={agentTenant}
          />
        </V2Container>
      }
    </V2Drawer>
  );
};

export default AreaDetailDrawer;

